import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import { emailLogService } from './emailLogService'
import type { FilaEstadoCliente, FilaMovimientoEliminado } from '@/lib/lineaDeTiempoCliente'

/** Techo de filas de cada lectura de `vista_audit_log` de la línea de tiempo. */
export const LIMITE_AUDITORIA = 500

/**
 * Resumen de cuenta corriente de un cliente para la ficha 360 (ORR-141).
 * Agrega las dimensiones que la vista de saldo (fuente única, ORR-140) no expone:
 * antigüedad de deuda, último pago / días sin pago y última notificación de deuda.
 * El saldo NO se calcula aquí: viene de `cliente.saldo` (VistaSaldoCliente).
 */
export interface CuentaCorrienteResumen {
  mesesDeuda: number
  deudaMasAntigua: string | null
  ultimoPago: string | null
  diasSinPago: number | null
  ultimaNotificacion: string | null
}

export const cuentaCorrienteService = {
  async obtenerResumen(
    client: SupabaseClient<Database>,
    organizacionId: string,
    clienteId: string,
  ): Promise<CuentaCorrienteResumen> {
    const [morosoRow, ultimoPagoRow, ultimasNotifs] = await Promise.all([
      // Antigüedad de deuda — solo presente si el cliente es moroso (saldo>0 en vista_ranking_morosos).
      (client as unknown as SupabaseClient)
        .from('vista_ranking_morosos')
        .select('meses_deuda, deuda_mas_antigua')
        .eq('cliente_id', clienteId)
        .maybeSingle(),
      // Último pago — misma lógica que reportesService.obtenerDeudores.
      client
        .from('movimientos')
        .select('fecha')
        .eq('cliente_id', clienteId)
        .eq('tipo', 'Pago')
        .order('fecha', { ascending: false })
        .limit(1)
        .maybeSingle(),
      // Última vez notificado sobre deuda: estado de cuenta, manual o por mail, o aviso automático de cobranza.
      emailLogService.ultimoEnvioPorClienteIds(client, [clienteId], ['estado_cuenta', 'cobranza_aviso'], organizacionId),
    ])

    if (morosoRow.error) throw morosoRow.error
    if (ultimoPagoRow.error) throw ultimoPagoRow.error

    const moroso = morosoRow.data as { meses_deuda: number | null; deuda_mas_antigua: string | null } | null
    const ultimoPago = (ultimoPagoRow.data as { fecha: string } | null)?.fecha ?? null
    const diasSinPago = ultimoPago
      ? Math.floor((Date.now() - new Date(ultimoPago).getTime()) / (1000 * 60 * 60 * 24))
      : null

    return {
      mesesDeuda: moroso?.meses_deuda ?? 0,
      deudaMasAntigua: moroso?.deuda_mas_antigua ?? null,
      ultimoPago,
      diasSinPago,
      ultimaNotificacion: ultimasNotifs[clienteId] ?? null,
    }
  },
  /** Altas y ediciones del cliente en `vista_audit_log`, de la más vieja a la más nueva, con el estado que guardó cada edición. */
  async obtenerEstadosCliente(client: SupabaseClient<Database>, clienteId: string): Promise<FilaEstadoCliente[]> {
    const { data, error } = await client
      .from('vista_audit_log')
      .select('id, accion, created_at, user_email, estado:detalle->cambios->>estado')
      .eq('entidad', 'cliente')
      .eq('entidad_id', clienteId)
      .in('accion', ['crear', 'editar'])
      .order('created_at', { ascending: true })
      .limit(LIMITE_AUDITORIA)

    if (error) throw error
    return (data ?? []) as FilaEstadoCliente[]
  },

  /**
   * Movimientos del cliente borrados, según `vista_audit_log`, del más nuevo al más viejo.
   *
   * Un borrado cuenta si su detalle trae el `cliente_id`, o si el `movimiento/crear` del mismo movimiento lo
   * trae. `registrado_el` es la fecha de ese `crear`, o null si no hay.
   */
  async obtenerMovimientosEliminados(
    client: SupabaseClient<Database>,
    clienteId: string,
  ): Promise<FilaMovimientoEliminado[]> {
    const creados = await client
      .from('vista_audit_log')
      .select('entidad_id, created_at')
      .eq('entidad', 'movimiento')
      .eq('accion', 'crear')
      .eq('detalle->>cliente_id', clienteId)
      .limit(LIMITE_AUDITORIA)

    if (creados.error) throw creados.error

    const registradoEl = new Map<string, string | null>()
    for (const fila of creados.data ?? []) {
      if (fila.entidad_id) registradoEl.set(fila.entidad_id, fila.created_at)
    }
    const ids = [...registradoEl.keys()]
    const filtro = [
      `detalle->>cliente_id.eq.${clienteId}`,
      ...(ids.length > 0 ? [`entidad_id.in.(${ids.join(',')})`] : []),
    ].join(',')

    const { data, error } = await client
      .from('vista_audit_log')
      .select('id, entidad_id, created_at, user_email, detalle')
      .eq('entidad', 'movimiento')
      .eq('accion', 'eliminar')
      .or(filtro)
      .order('created_at', { ascending: false })
      .limit(LIMITE_AUDITORIA)

    if (error) throw error
    // Los tipos de la vista declaran nullable cada columna. id y user_email salen de columnas NOT NULL de audit_log.
    return (data ?? []).map((fila) => ({
      id: fila.id as string,
      created_at: fila.created_at,
      user_email: fila.user_email as string,
      detalle: fila.detalle,
      registrado_el: (fila.entidad_id && registradoEl.get(fila.entidad_id)) || null,
    }))
  },
}
